import React, { useEffect, useMemo, useState } from 'react';

import {
  getUserInfo,
  getUserManagementAvailableFeatureFlags,
  getUserManagementUserFeatureFlags,
  updateUserManagementUserFeatureFlags,
} from 'apis/UserAPI';
import { useRootContext } from 'components/layout/RootContext';
import Modal from 'components/lib/Modal';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleUpdateUser } from 'store/slices/authSlice';

import { hasUserManagementFeatureFlagChanges } from './helpers';
import {
  UserManagementFeatureFlagKeyType,
  UserManagementFeatureFlagOptionType,
  UserManagementFeatureFlagsType,
  UserManagementUserType,
} from './types';
import UserManagementFeatureFlagsModalContent from './UserManagementFeatureFlagsModalContent';
import UserManagementFeatureFlagsModalFooter from './UserManagementFeatureFlagsModalFooter';
import UserManagementFeatureFlagsModalHeader from './UserManagementFeatureFlagsModalHeader';
import UserManagementFeatureFlagsModalSingleList from './UserManagementFeatureFlagsModalSingleList';
import UserManagementFeatureFlagsModalStatus from './UserManagementFeatureFlagsModalStatus';
import UserManagementFeatureFlagsModalUserIntro from './UserManagementFeatureFlagsModalUserIntro';
import { STATUS } from '../../constants';

type Props = {
  open: boolean;
  onClose: () => void;
  user?: UserManagementUserType;
  onSaved?: () => void;
};

const UserManagementSingleUserFeatureFlagsModal = ({ open, onClose, user, onSaved }: Props) => {
  const dispatch = useAppDispatch();
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);

  const [status, setStatus] = useState(STATUS.IDLE);
  const [saveStatus, setSaveStatus] = useState(STATUS.IDLE);
  const [availableFlags, setAvailableFlags] = useState<UserManagementFeatureFlagOptionType[]>([]);
  const [featureFlags, setFeatureFlags] = useState<UserManagementFeatureFlagsType | null>(null);
  const [initialFeatureFlags, setInitialFeatureFlags] = useState<UserManagementFeatureFlagsType | null>(null);

  const userId = user?.id;

  useEffect(() => {
    if (!open) {
      setStatus(STATUS.IDLE);
      setSaveStatus(STATUS.IDLE);
      setAvailableFlags([]);
      setFeatureFlags(null);
      setInitialFeatureFlags(null);
      return;
    }

    let isCancelled = false;

    const loadFeatureFlags = async () => {
      try {
        setStatus(STATUS.LOADING);

        const [nextAvailableFlags, nextFeatureFlags] = await Promise.all([
          getUserManagementAvailableFeatureFlags(),
          userId ? getUserManagementUserFeatureFlags(userId) : Promise.resolve(null),
        ]);

        if (isCancelled) {
          return;
        }

        setAvailableFlags(nextAvailableFlags);
        setFeatureFlags(nextFeatureFlags);
        setInitialFeatureFlags(nextFeatureFlags);
        setStatus(STATUS.LOADED);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setStatus(STATUS.FAILURE);
        showMessage('error', 'Failed to load feature flags');
        console.error('Failed to load feature flags', error);
      }
    };

    loadFeatureFlags();

    return () => {
      isCancelled = true;
    };
  }, [open, showMessage, userId]);

  const hasChanges = useMemo(
    () => hasUserManagementFeatureFlagChanges(false, {}, featureFlags, initialFeatureFlags),
    [featureFlags, initialFeatureFlags]
  );

  const handleFlagChange = (key: UserManagementFeatureFlagKeyType, checked: boolean) => {
    if (!featureFlags) {
      return;
    }

    setFeatureFlags({
      ...featureFlags,
      [key]: checked,
    });
  };

  const handleSave = async () => {
    if (!user || !featureFlags) {
      return;
    }

    try {
      setSaveStatus(STATUS.LOADING);

      const updatedFeatureFlags = await updateUserManagementUserFeatureFlags(user.id, featureFlags);

      if (currentUser?.PAUser_Id === user.id) {
        const updatedCurrentUser = await getUserInfo();
        dispatch(handleUpdateUser(updatedCurrentUser));
      }

      setFeatureFlags(updatedFeatureFlags);
      setInitialFeatureFlags(updatedFeatureFlags);
      showMessage('success', 'Feature flags updated!');
      onSaved?.();
      onClose();
    } catch (error) {
      showMessage('error', 'Failed to update feature flags');
      console.error('Failed to update feature flags', error);
    } finally {
      setSaveStatus(STATUS.IDLE);
    }
  };

  const isLoading = status === STATUS.LOADING;
  const isSaving = saveStatus === STATUS.LOADING;

  return (
    <Modal
      title={<UserManagementFeatureFlagsModalHeader isBulkMode={false} selectedUsersCount={0} />}
      open={open}
      centered
      width={620}
      destroyOnClose
      onCancel={onClose}
      footer={
        <UserManagementFeatureFlagsModalFooter
          hasChanges={hasChanges}
          isLoading={isLoading}
          isSaving={isSaving}
          onClose={onClose}
          onSave={handleSave}
        />
      }
    >
      <UserManagementFeatureFlagsModalContent>
        <UserManagementFeatureFlagsModalUserIntro user={user} />
        <UserManagementFeatureFlagsModalStatus
          availableFlagsCount={availableFlags.length}
          isLoading={isLoading}
          status={status}
        />
        {!isLoading && status !== STATUS.FAILURE && featureFlags && availableFlags.length > 0 && (
          <UserManagementFeatureFlagsModalSingleList
            availableFlags={availableFlags}
            featureFlags={featureFlags}
            initialFeatureFlags={initialFeatureFlags}
            onFlagChange={handleFlagChange}
          />
        )}
      </UserManagementFeatureFlagsModalContent>
    </Modal>
  );
};

export default UserManagementSingleUserFeatureFlagsModal;
